import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  type CurrencyCode,
} from "@majourneys/shared";
import { formatNumber } from "./format";
import { currencyForCountry, fetchGeoCountry } from "./geo";
import { ratesFromBody, writeCurrencyCookie, type FxSnapshot } from "./ssr";

/**
 * THE currency context. Not one of several.
 *
 * This module exists because there used to be two of it — one in
 * `apps/majourneys/src/lib/currency.tsx` and a fork in
 * `packages/booking/src/lib/currency.tsx` — each with its own `createContext`
 * call and its own copy of the rate table. Only the app's provider was ever
 * mounted, and a React context is identified by the object, not the name, so
 * every `useCurrency()` inside `@majourneys/booking` silently fell through to
 * the "no provider" branch and returned MAD.
 *
 * Measured 2026-08-21 with one guest on `mj.currency=USD`: the app's own
 * activity cards rendered "100 USD" while the checkout, thank-you, sidebar,
 * package cards and booking summary — every surface that comes from the
 * booking package — rendered "1,000 MAD" on the same screen. The switcher
 * looked like it worked, because the surfaces a guest checks first were the
 * ones that happened to be wired.
 *
 * So: one module, one context, one rate table, imported by both. Re-exporting
 * a fork would not have fixed it; the fork had to stop existing.
 */

export type { CurrencyCode };

export const CURRENCY_CODES: readonly CurrencyCode[] = SUPPORTED_CURRENCIES;

/**
 * Last-resort display rates, applied to MAD-denominated catalogue prices.
 *
 * Only ever used for PAY-ON-ARRIVAL activities, where the guest hands over
 * dirhams on the day and this is a courtesy conversion so they can judge the
 * cost. Anything charged online (stays) must be quoted by the supplier in the
 * guest's currency instead — see `currency` on stayPropertyListQuerySchema —
 * because a converted figure would not match the card statement.
 *
 * These are a FALLBACK now, not the source. They were the source, frozen in
 * the bundle, while the API converted with live rates plus a 5% margin — so
 * the app and the site quoted different money for the same activity. On
 * 2026-09-04 a 950 MAD jeep ride read $106.72 in the app and $95.00 on the
 * site, 12% apart, because 0.1 had never been refreshed. The provider now
 * pulls /currency/rates and only falls back to this table when that fails.
 */
const FALLBACK_RATES_FROM_MAD: Record<CurrencyCode, number> = {
  MAD: 1,
  USD: 0.1,
  EUR: 0.092,
  GBP: 0.079,
};

const SYMBOLS: Record<CurrencyCode, string> = {
  MAD: "د.م.",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

const STORAGE_KEY = "mj.currency";
/** The last live rates this browser saw, so a page without a server snapshot never starts from the bundled table. */
const RATES_KEY = "mj.rates";

/** Returns the user's explicitly-chosen currency, or null if they never picked one. */
function readStoredChoice(): CurrencyCode | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return CURRENCY_CODES.includes(raw as CurrencyCode) ? (raw as CurrencyCode) : null;
  } catch {
    return null;
  }
}

function readStoredRates(): Record<CurrencyCode, number> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(RATES_KEY);
    return raw ? ratesFromBody({ rates: JSON.parse(raw) }) : null;
  } catch {
    return null;
  }
}

function sameRates(a: Record<CurrencyCode, number>, b: Record<CurrencyCode, number>): boolean {
  return CURRENCY_CODES.every((code) => a[code] === b[code]);
}

type CurrencyContextValue = {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  /** 1 MAD → code, margin already applied by the API. */
  rates: Record<CurrencyCode, number>;
  /**
   * True once the guest has picked a currency on this device (header, account
   * tab), false while it is only a guess from their country or the default.
   * The account's saved currency may only fill the second case: the pick a
   * guest just made in the header must never be overridden by it.
   */
  explicit: boolean;
  /** False until the browser has read its stored choice, so `explicit` is not yet final. */
  resolved: boolean;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({
  children,
  ratesUrl,
  initial,
}: {
  children: ReactNode;
  /**
   * Absolute URL of the API's /currency/rates. Omit it and the fallback table
   * is used — which is what every non-web consumer of this package wants.
   */
  ratesUrl?: string;
  /**
   * What the server rendered with (see ./ssr.ts). The browser must start from
   * the same snapshot or hydration flashes the old prices back. When it is
   * present the first paint is already in the guest's currency at the live
   * rate, and nothing below swaps a number unless the guest's choice really
   * differs from what the server guessed.
   */
  initial?: FxSnapshot | null;
}) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(initial?.currency ?? DEFAULT_CURRENCY);
  const [rates, setRates] = useState<Record<CurrencyCode, number>>(initial?.rates ?? FALLBACK_RATES_FROM_MAD);
  const [explicit, setExplicit] = useState<boolean>(initial?.explicit ?? false);
  const [resolved, setResolved] = useState(false);
  const hasServerRates = Boolean(initial?.rates);
  const serverCurrencyKnown = Boolean(initial);

  useEffect(() => {
    // No server snapshot: the last live rates this browser saw beat the
    // bundled table, which is months old. The fetch below still refreshes.
    if (!hasServerRates) {
      const stored = readStoredRates();
      if (stored) setRates((prev) => (sameRates(prev, stored) ? prev : stored));
    }
    if (!ratesUrl) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await fetch(ratesUrl, { signal: controller.signal });
        if (!res.ok) return;
        const next = ratesFromBody(await res.json(), FALLBACK_RATES_FROM_MAD);
        if (!next) return;
        // Same numbers as the server snapshot (the normal case: the API
        // refreshes daily) leave the state untouched, so nothing re-renders.
        setRates((prev) => (sameRates(prev, next) ? prev : next));
        try {
          window.localStorage.setItem(RATES_KEY, JSON.stringify(next));
        } catch {
          // Private mode: the server snapshot still covers the next page.
        }
      } catch {
        // Keep what we have; a stale rate beats no price.
      }
    })();
    return () => controller.abort();
  }, [ratesUrl, hasServerRates]);

  useEffect(() => {
    const choice = readStoredChoice();
    if (choice) {
      // The visitor explicitly picked a currency — always honour it, never auto-detect.
      setCurrencyState(choice);
      setExplicit(true);
      setResolved(true);
      // Guests who picked before the cookie existed: hand it to the server so
      // the next page is rendered in their currency from the first byte.
      writeCurrencyCookie(choice);
      return;
    }
    setResolved(true);
    // The server already guessed from the country (same rule as below), or
    // read a cookie this browser no longer has in localStorage.
    if (serverCurrencyKnown) return;
    // No manual choice yet: detect from the visitor's country (via the edge
    // /__geo endpoint) and apply it to state only — without persisting, so a
    // later explicit pick is what gets remembered.
    const controller = new AbortController();
    void fetchGeoCountry(controller.signal).then((country) => {
      if (country) setCurrencyState(currencyForCountry(country));
    });
    return () => controller.abort();
  }, [serverCurrencyKnown]);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    setExplicit(true);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(STORAGE_KEY, code);
      } catch {
        // The cookie below still carries it.
      }
      writeCurrencyCookie(code);
    }
  }, []);

  const value = useMemo(
    () => ({ currency, setCurrency, rates, explicit, resolved }),
    [currency, setCurrency, rates, explicit, resolved],
  );
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    return {
      currency: DEFAULT_CURRENCY,
      setCurrency: () => {},
      rates: FALLBACK_RATES_FROM_MAD,
      explicit: false,
      resolved: true,
    };
  }
  return ctx;
}

/**
 * Fallback-table conversion, for callers with no provider above them. Inside
 * the app prefer useDisplayPrice / useDisplayPriceParts, which use the live
 * rates the API priced with.
 */
export function convertFromMad(
  madValue: number,
  target: CurrencyCode,
  rates: Record<CurrencyCode, number> = FALLBACK_RATES_FROM_MAD,
): number {
  const rate = rates[target] ?? 1;
  return madValue * rate;
}

export function currencyLabelFor(currency: CurrencyCode, lang: string): string {
  if (currency === "MAD") return lang === "ar" ? "درهم" : "MAD";
  return currency;
}

export function currencySymbolFor(currency: CurrencyCode): string {
  return SYMBOLS[currency] ?? currency;
}

/**
 * Rounds a converted amount to a whole number in every currency.
 *
 * Owner rule (2026-09-28): prices never show decimals. "164.01 USD" reads as
 * a meter reading, not a price; "164 USD" is what a guest expects. The same
 * applies to euros and pounds, not just dirhams.
 */
function roundForDisplay(converted: number): number {
  return Math.round(converted);
}

/** Whole numbers only, so Intl never adds a fraction back. */
const WHOLE: Intl.NumberFormatOptions = { maximumFractionDigits: 0 };

/**
 * Returns a function that formats a MAD-denominated value into the user's
 * selected currency. Use everywhere prices were previously hardcoded as MAD.
 */
export function useDisplayPrice() {
  const { currency, rates } = useCurrency();
  const { i18n } = useTranslation();
  return useCallback(
    (madValue: number) => {
      const rounded = roundForDisplay(convertFromMad(madValue, currency, rates));
      return `${formatNumber(rounded, i18n.language, WHOLE)} ${currencyLabelFor(currency, i18n.language)}`;
    },
    [currency, rates, i18n.language],
  );
}

export function useDisplayPriceParts() {
  const { currency, rates } = useCurrency();
  const { i18n } = useTranslation();
  return useCallback(
    (madValue: number) => {
      const rounded = roundForDisplay(convertFromMad(madValue, currency, rates));
      return {
        value: formatNumber(rounded, i18n.language, WHOLE),
        currency: currencyLabelFor(currency, i18n.language),
        code: currency,
      };
    },
    [currency, rates, i18n.language],
  );
}

/** Localized currency label tied to the live selection. */
export function useCurrencyLabel(): string {
  const { currency } = useCurrency();
  const { i18n } = useTranslation();
  return currencyLabelFor(currency, i18n.language);
}

/**
 * Money for a surface that shows a total under its lines: the booking flow.
 *
 * `unit` converts ONE dirham price and rounds it, exactly as useDisplayPrice
 * would show it. Build a total from those rounded prices and it is the sum of
 * what the lines say: "2 x 33 USD" over 66, not the 65 that converting the
 * 600 MAD total in one go gives (owner, 2026-09-29). `parts` and `format`
 * label an amount that is ALREADY in the display currency; do not pass them
 * dirhams. Display only: the checkout is sent dirhams.
 *
 * Zero, negatives (the -1 "band not offered" sentinel) and non-numbers pass
 * through `unit` untouched, so pricing rules that test for them still work.
 */
export type DisplayMoney = {
  unit: (mad: number) => number;
  parts: (amount: number) => { value: string; currency: string };
  format: (amount: number) => string;
};

export function displayMoneyFor(
  currency: CurrencyCode,
  rates: Record<CurrencyCode, number>,
  lang: string,
): DisplayMoney {
  const label = currencyLabelFor(currency, lang);
  const unit = (mad: number) =>
    typeof mad === "number" && Number.isFinite(mad) && mad > 0
      ? roundForDisplay(convertFromMad(mad, currency, rates))
      : mad;
  const parts = (amount: number) => ({ value: formatNumber(Math.round(amount), lang, WHOLE), currency: label });
  return { unit, parts, format: (amount) => `${parts(amount).value} ${label}` };
}

/** `displayMoneyFor`, bound to the live currency, rates and language. */
export function useDisplayMoney(): DisplayMoney {
  const { currency, rates } = useCurrency();
  const { i18n } = useTranslation();
  return useMemo(() => displayMoneyFor(currency, rates, i18n.language), [currency, rates, i18n.language]);
}
